import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { environment as env } from '../../environments/environment';

// SPA client for the F2-ADMIN.SymbolLists REGISTRY surface (IT-F2-43,
// registry-in-Mongo rework 2026-07-23). Each doc is a per-list pointer:
// which GitHub repo/branch/path holds the bytes, what format, which
// column is the ticker. Editing a registry doc is a code-free way to
// add a new list, swap the repo behind an existing name, or pin the
// ticker column on a csv with a nonstandard header.
//
// Content edits (the actual symbol lines) still flow through the
// existing CommonSymbolListsService.upsertFile which commits to
// GitHub — this service is registry-only.

export interface SymbolListRegistryDoc {
  _id: string;                          // list name
  label?: string;                       // human-friendly display
  active?: boolean;                     // toggle without deleting
  repo?: string;                        // "<owner>/<name>"
  branch?: string;                      // default main
  path?: string;                        // e.g. lists/spx.csv
  format?: 'txt' | 'csv';
  ticker_column?: string;               // csv only, case-insensitive header
  customer_slug?: string | null;        // scope: null = fleet-wide, else restrict to customer
  scanner_slug?: string | null;         // scope: null = every scanner (for the customer if set)
  // IT-F2-230: per-list role gates. reader_role controls who sees the
  // list in the client app's Common Data section; writer_role controls
  // who can PATCH file content. Both default to 'admin' on create so
  // admin explicitly opts a list into the client-editable pool.
  reader_role?: 'admin' | 'client';
  writer_role?: 'admin' | 'client';
  created_at?: string;
  created_by_email?: string;
  updated_at?: string;
  updated_by_email?: string;
}

// IT-F2-230 Sub 4: per-commit projection for the History section on
// the registry-entry detail. Backend fetches from GitHub commits API,
// keeps the payload compact — sha, first line of message, ISO time,
// author name+email, deep-link to github.com for the full diff.
export interface HistoryCommit {
  sha: string;
  message: string;
  at: string;
  author_name: string;
  author_email: string;
  html_url: string;
}

export interface RegistryUpsertResult {
  ok: true;
  name: string;
  action: 'created' | 'updated';
  doc: SymbolListRegistryDoc;
  refresh?: any;
}

@Injectable({ providedIn: 'root' })
export class SymbolListsRegistryService {
  constructor(
    private http: HttpClient,
    private loadingService: LoadingService,
    private toast: ToastService,
  ) {}

  private _opts() { return { withCredentials: true }; }
  private _base() { return `${env.apiUrl}/rest/symbol-lists/registry`; }

  async listAll(): Promise<SymbolListRegistryDoc[]> {
    const id = this.loadingService.add('Loading symbol-lists registry');
    const url = this._base();
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return Array.isArray(res?.entries) ? res.entries as SymbolListRegistryDoc[] : [];
    } catch (e: any) {
      this._toastErr('Registry', e, 'F2A_SYMBOL_LISTS_REGISTRY_LIST_FAIL', url);
      return [];
    } finally {
      this.loadingService.remove(id);
    }
  }

  async getOne(name: string): Promise<SymbolListRegistryDoc | null> {
    const id = this.loadingService.add(`Loading registry ${name}`);
    const url = `${this._base()}/${encodeURIComponent(name)}`;
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return res?.entry ? (res.entry as SymbolListRegistryDoc) : null;
    } catch (e: any) {
      if (e?.status === 404) return null;
      this._toastErr('Registry', e, 'F2A_SYMBOL_LISTS_REGISTRY_GET_FAIL', url);
      return null;
    } finally {
      this.loadingService.remove(id);
    }
  }

  // Create a new registry entry. `name`, `repo`, `path` are required by
  // the backend; the SPA form enforces this too so the error surface
  // stays quiet.
  async create(params: {
    name: string;
    repo: string;
    path: string;
    branch?: string;
    format?: 'txt' | 'csv';
    ticker_column?: string;
    label?: string;
    active?: boolean;
    customer_slug?: string | null;
    scanner_slug?: string | null;
    reader_role?: 'admin' | 'client';
    writer_role?: 'admin' | 'client';
  }): Promise<RegistryUpsertResult | null> {
    const id = this.loadingService.add(`Creating registry ${params.name}`);
    const url = this._base();
    try {
      const res: any = await this.http.post(url, params, this._opts()).toPromise();
      return res?.ok ? (res as RegistryUpsertResult) : null;
    } catch (e: any) {
      this._toastErr('Create failed', e, 'F2A_SYMBOL_LISTS_REGISTRY_CREATE_FAIL', url);
      return null;
    } finally {
      this.loadingService.remove(id);
    }
  }

  // Update an existing registry entry. Any unset field leaves the
  // existing value alone (backend semantics).
  async update(
    name: string,
    patch: Partial<Omit<SymbolListRegistryDoc, '_id' | 'created_at' | 'created_by_email'>>,
  ): Promise<RegistryUpsertResult | null> {
    const id = this.loadingService.add(`Saving registry ${name}`);
    const url = `${this._base()}/${encodeURIComponent(name)}`;
    try {
      const res: any = await this.http.put(url, patch, this._opts()).toPromise();
      return res?.ok ? (res as RegistryUpsertResult) : null;
    } catch (e: any) {
      this._toastErr('Save failed', e, 'F2A_SYMBOL_LISTS_REGISTRY_UPDATE_FAIL', url);
      return null;
    } finally {
      this.loadingService.remove(id);
    }
  }

  // GET /registry/lookup/branches?repo=<owner/name>
  async lookupBranches(repo: string): Promise<string[]> {
    const id = this.loadingService.add(`Fetching branches for ${repo}`);
    const url = `${this._base()}/lookup/branches?repo=${encodeURIComponent(repo)}`;
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return Array.isArray(res?.branches) ? res.branches as string[] : [];
    } catch (e: any) {
      // Silent-return on lookup failures — the dropdown is a nice-to-have,
      // the raw text input still works. Don't toast every hiccup.
      return [];
    } finally {
      this.loadingService.remove(id);
    }
  }

  // GET /registry/lookup/paths?repo=<owner/name>&branch=<branch>&all=1?
  async lookupPaths(repo: string, branch: string, all = false): Promise<string[]> {
    const id = this.loadingService.add(`Fetching paths for ${repo}@${branch}`);
    const url = `${this._base()}/lookup/paths?repo=${encodeURIComponent(repo)}&branch=${encodeURIComponent(branch)}${all ? '&all=1' : ''}`;
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return Array.isArray(res?.paths) ? res.paths as string[] : [];
    } catch (e: any) {
      return [];
    } finally {
      this.loadingService.remove(id);
    }
  }

  // IT-F2-230 Sub 4: git commit log for the underlying file. Backend
  // reads GitHub /repos/:owner/:name/commits?path=<file>&sha=<branch>.
  // Returns [] on failure (empty history is a valid empty state).
  async listHistory(name: string, perPage = 50): Promise<HistoryCommit[]> {
    const id = this.loadingService.add(`Loading history for ${name}`);
    const url = `${env.apiUrl}/rest/symbol-lists/files/${encodeURIComponent(name)}/history?per_page=${perPage}`;
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return Array.isArray(res?.commits) ? res.commits as HistoryCommit[] : [];
    } catch (e: any) {
      this._toastErr('History', e, 'F2A_SYMBOL_LISTS_HISTORY_FAIL', url);
      return [];
    } finally {
      this.loadingService.remove(id);
    }
  }

  async remove(name: string): Promise<boolean> {
    const id = this.loadingService.add(`Deleting registry ${name}`);
    const url = `${this._base()}/${encodeURIComponent(name)}`;
    try {
      const res: any = await this.http.delete(url, this._opts()).toPromise();
      return !!res?.ok;
    } catch (e: any) {
      this._toastErr('Delete failed', e, 'F2A_SYMBOL_LISTS_REGISTRY_DELETE_FAIL', url);
      return false;
    } finally {
      this.loadingService.remove(id);
    }
  }

  private _toastErr(title: string, e: any, code: string, requestUrl?: string) {
    const msg = e?.error?.errors?.[0]?.message || e?.message || 'Unknown error';
    this.toast.error(title, msg, {
      error_code: code,
      customer: 'f2-admin',
      scanner_path: '/admin/common-data',
      request_url: requestUrl || e?.url || undefined,
      http_status: e?.status ?? 0,
      content_type: (e?.headers?.get?.('content-type') as string | undefined) || (e?.error && typeof e.error === 'string' ? 'text/html' : 'application/json'),
      f2_response_body: (typeof e?.error === 'string' ? e.error : JSON.stringify(e?.error || {})).slice(0, 500),
      f2_response_message: msg,
    });
  }
}
